import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI, permissionsAPI, templateAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { 
  Shield, Users, ChevronRight, Loader2, Save, RotateCcw, AlertTriangle,
  LayoutDashboard, Package, Warehouse, Truck, ShoppingCart, ClipboardCheck,
  RefreshCw, DollarSign, UserCog, MessageSquare, Activity, Settings, Briefcase,
  ClipboardList, Wrench, BookTemplate, ChevronDown, Trash2, Pencil, X, Check
} from 'lucide-react';
import { toast } from 'sonner';

// Permission category icons
const CATEGORY_ICONS = {
  dashboard: LayoutDashboard,
  catalogue: Package,
  warehouse: Warehouse,
  fleet: Truck,
  po: ShoppingCart,
  approvals: ClipboardCheck,
  reorder: RefreshCw,
  pricing: DollarSign,
  team: UserCog,
  messages: MessageSquare,
  activity: Activity,
  settings: Settings,
  jobs: Briefcase,
  parts: ClipboardList,
  inventory_tools: Wrench,
};

// Category labels
const CATEGORY_LABELS = {
  dashboard: "Dashboard & Visibility",
  catalogue: "Parts List",
  warehouse: "Warehouse",
  fleet: "Vehicles / Fleet",
  po: "Purchase Orders",
  approvals: "Approvals Queue",
  reorder: "Reorder",
  pricing: "Pricing Sensitivity",
  team: "Team / Users",
  messages: "Messages",
  activity: "Activity / Audit",
  settings: "Settings / Access Control",
  jobs: "Jobs",
  parts: "Part Requests",
  inventory_tools: "Inventory Tools",
};

export const ManageAccess = () => {
  const navigate = useNavigate();
  const { hasPerm, isOwner } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissionDefs, setPermissionDefs] = useState({});
  const [roleDefaults, setRoleDefaults] = useState({});
  const [userPermissions, setUserPermissions] = useState({});
  const [editedPermissions, setEditedPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Template state
  const [templates, setTemplates] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showApplyDropdown, setShowApplyDropdown] = useState(false);
  const [showApplyConfirm, setShowApplyConfirm] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [editingTemplateName, setEditingTemplateName] = useState('');

  // Check access
  useEffect(() => {
    if (!isOwner && !hasPerm('settings.manage_access')) {
      navigate('/dashboard');
      toast.error('Access denied');
    }
  }, [isOwner, hasPerm, navigate]);

  // Fetch users, permission definitions, and templates
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, permsRes, templatesRes] = await Promise.all([
        usersAPI.list(),
        permissionsAPI.getAllPermissions(),
        templateAPI.list(),
      ]);
      setUsers(usersRes.data.filter(u => u.is_active));
      setPermissionDefs(permsRes.data.permissions || {});
      setRoleDefaults(permsRes.data.role_defaults || {});
      setTemplates(templatesRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch selected user's permissions
  const fetchUserPermissions = useCallback(async (userId) => {
    try {
      const res = await permissionsAPI.getUserPermissions(userId);
      setSelectedUser(res.data);
      setUserPermissions(res.data.effective_permissions || {});
      setEditedPermissions(res.data.effective_permissions || {});
    } catch (error) {
      toast.error('Failed to load user permissions');
    }
  }, []);

  const handleSelectUser = (userId) => {
    setSelectedUserId(userId);
    fetchUserPermissions(userId);
    setShowApplyDropdown(false);
  };

  const handlePermissionToggle = (key, checked) => {
    setEditedPermissions(prev => ({ ...prev, [key]: checked }));
  };

  const isModified = (key) => editedPermissions[key] !== userPermissions[key];
  const hasChanges = Object.keys(editedPermissions).some(key => isModified(key));

  const handleRoleChange = async (newRole) => {
    if (!selectedUserId) return;
    setSaving(true);
    try {
      await permissionsAPI.updateUserRole(selectedUserId, newRole);
      toast.success(`Role updated to ${newRole}`);
      await fetchUserPermissions(selectedUserId);
      const usersRes = await usersAPI.list();
      setUsers(usersRes.data.filter(u => u.is_active));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!selectedUserId) return;
    setSaving(true);
    try {
      const permissionUpdates = Object.entries(editedPermissions).map(([key, allowed]) => ({ key, allowed }));
      await permissionsAPI.updateUserPermissions(selectedUserId, permissionUpdates);
      toast.success('Permissions saved');
      setUserPermissions(editedPermissions);
      // Refresh user list to clear/update template badge
      const usersRes = await usersAPI.list();
      setUsers(usersRes.data.filter(u => u.is_active));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedUserId) return;
    setResetting(true);
    try {
      const res = await permissionsAPI.resetUserPermissions(selectedUserId);
      toast.success('Permissions reset to role defaults');
      setUserPermissions(res.data.effective_permissions || {});
      setEditedPermissions(res.data.effective_permissions || {});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset permissions');
    } finally {
      setResetting(false);
    }
  };

  const handleCancel = () => setEditedPermissions(userPermissions);

  // --- Template handlers ---

  const handleSaveTemplate = async () => {
    const name = templateName.trim();
    if (!name) { toast.error('Template name is required'); return; }

    const enabledKeys = Object.entries(editedPermissions)
      .filter(([, v]) => v)
      .map(([k]) => k);

    if (enabledKeys.length === 0) { toast.error('Select at least one permission'); return; }

    setSavingTemplate(true);
    try {
      await templateAPI.create({ name, permissions: enabledKeys });
      toast.success(`Template "${name}" saved`);
      setShowSaveModal(false);
      setTemplateName('');
      const res = await templateAPI.list();
      setTemplates(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleApplyTemplate = async (template) => {
    if (!selectedUserId) return;
    setApplyingTemplate(true);
    try {
      const res = await templateAPI.apply(template.id, selectedUserId);
      toast.success(res.data.message);
      setUserPermissions(res.data.effective_permissions || {});
      setEditedPermissions(res.data.effective_permissions || {});
      setShowApplyConfirm(null);
      setShowApplyDropdown(false);
      // Refresh user list to update badge
      const usersRes = await usersAPI.list();
      setUsers(usersRes.data.filter(u => u.is_active));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply template');
    } finally {
      setApplyingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId, e) => {
    e.stopPropagation();
    try {
      await templateAPI.remove(templateId);
      toast.success('Template deleted');
      setTemplates(prev => prev.filter(t => t.id !== templateId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleRenameTemplate = async (templateId) => {
    const name = editingTemplateName.trim();
    if (!name) return;
    try {
      await templateAPI.update(templateId, { name });
      toast.success('Template renamed');
      setTemplates(prev => prev.map(t => t.id === templateId ? { ...t, name } : t));
      setEditingTemplateId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const selectedUserData = users.find(u => u.id === selectedUserId);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground flex items-center gap-2">
            <Shield className="w-7 h-7 text-primary" />
            Manage Access
          </h1>
          <p className="text-muted-foreground mt-1">Control user roles and permissions</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* User List */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5" />
                Team Members
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {users.map((user) => (
                  <button
                    key={user.id}
                    data-testid={`user-row-${user.id}`}
                    onClick={() => handleSelectUser(user.id)}
                    className={`w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors flex items-center justify-between ${
                      selectedUserId === user.id ? 'bg-primary/5 border-l-2 border-primary' : ''
                    }`}
                  >
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        user.role === 'owner' ? 'bg-amber-100 text-amber-700' :
                        user.role === 'office' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {user.role === 'owner' ? 'Owner' : user.role === 'office' ? 'Office Staff' : 'Technician'}
                      </span>
                      {user.last_applied_template_name && (
                        <span
                          className="text-xs font-medium px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 ml-1"
                          data-testid={`template-badge-${user.id}`}
                        >
                          Template: {user.last_applied_template_name}
                        </span>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Permission Editor */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">
                {selectedUser ? `${selectedUser.user_name}'s Permissions` : 'Select a User'}
              </CardTitle>
              {selectedUser && (
                <CardDescription>
                  Customize permissions or use role defaults
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {!selectedUser ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Select a team member to manage their permissions</p>
                </div>
              ) : selectedUser.is_owner ? (
                <div className="text-center py-12">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
                  <p className="text-lg font-semibold text-amber-700">Owner Has Full Access</p>
                  <p className="text-muted-foreground mt-2">
                    Owners automatically have all permissions and cannot be restricted.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Role Selector + Template Actions */}
                  <div className="flex flex-wrap items-center gap-3 pb-4 border-b">
                    <Label className="w-14 shrink-0">Role</Label>
                    <Select
                      value={selectedUser.role}
                      onValueChange={handleRoleChange}
                      disabled={saving}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="office">Office Staff</SelectItem>
                        <SelectItem value="tech">Technician</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReset}
                      disabled={resetting || saving}
                    >
                      {resetting ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <RotateCcw className="w-4 h-4 mr-1" />
                      )}
                      Reset Defaults
                    </Button>

                    <div className="ml-auto flex items-center gap-2">
                      {/* Save as Template */}
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="save-as-template-btn"
                        onClick={() => { setShowSaveModal(true); setTemplateName(''); }}
                      >
                        <BookTemplate className="w-4 h-4 mr-1" />
                        Save as Template
                      </Button>

                      {/* Apply Template */}
                      {templates.length > 0 && (
                        <div className="relative">
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid="apply-template-btn"
                            onClick={() => setShowApplyDropdown(!showApplyDropdown)}
                          >
                            <ChevronDown className="w-4 h-4 mr-1" />
                            Apply Template
                          </Button>

                          {showApplyDropdown && (
                            <div
                              className="absolute right-0 top-full mt-1 w-72 bg-popover border rounded-lg shadow-lg z-50 py-1"
                              data-testid="template-dropdown"
                            >
                              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b">
                                Templates
                              </div>
                              {templates.map((tmpl) => (
                                <div
                                  key={tmpl.id}
                                  className="flex items-center justify-between px-3 py-2 hover:bg-muted/50 cursor-pointer group"
                                  data-testid={`template-item-${tmpl.id}`}
                                >
                                  {editingTemplateId === tmpl.id ? (
                                    <div className="flex items-center gap-1 flex-1 mr-2">
                                      <Input
                                        value={editingTemplateName}
                                        onChange={(e) => setEditingTemplateName(e.target.value)}
                                        className="h-7 text-sm"
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleRenameTemplate(tmpl.id);
                                          if (e.key === 'Escape') setEditingTemplateId(null);
                                        }}
                                        autoFocus
                                      />
                                      <button onClick={() => handleRenameTemplate(tmpl.id)} className="p-1 hover:text-green-600">
                                        <Check className="w-3.5 h-3.5" />
                                      </button>
                                      <button onClick={() => setEditingTemplateId(null)} className="p-1 hover:text-red-600">
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  ) : (
                                    <>
                                      <div
                                        className="flex-1"
                                        onClick={() => setShowApplyConfirm(tmpl)}
                                      >
                                        <p className="text-sm font-medium">{tmpl.name}</p>
                                        <p className="text-xs text-muted-foreground">{tmpl.permission_count} permissions</p>
                                      </div>
                                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingTemplateId(tmpl.id);
                                            setEditingTemplateName(tmpl.name);
                                          }}
                                          className="p-1 rounded hover:bg-muted"
                                          data-testid={`rename-template-${tmpl.id}`}
                                        >
                                          <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                                        </button>
                                        <button
                                          onClick={(e) => handleDeleteTemplate(tmpl.id, e)}
                                          className="p-1 rounded hover:bg-destructive/10"
                                          data-testid={`delete-template-${tmpl.id}`}
                                        >
                                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Permission Groups */}
                  <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
                    {Object.entries(permissionDefs).map(([category, data]) => {
                      const CategoryIcon = CATEGORY_ICONS[category] || Settings;
                      return (
                        <div key={category} className="space-y-3">
                          <h3 className="font-semibold text-sm text-foreground flex items-center gap-2 sticky top-0 bg-background py-1">
                            <CategoryIcon className="w-4 h-4 text-primary" />
                            {CATEGORY_LABELS[category] || data.label}
                          </h3>
                          <div className="space-y-2 pl-6">
                            {data.permissions?.map((perm) => {
                              const isChecked = editedPermissions[perm.key] ?? false;
                              const modified = isModified(perm.key);
                              const roleDefault = roleDefaults[selectedUser.role]?.[perm.key] ?? false;
                              
                              return (
                                <div
                                  key={perm.key}
                                  className={`flex items-center justify-between py-2 px-3 rounded-lg ${
                                    modified ? 'bg-amber-50 border border-amber-200' : 'hover:bg-muted/50'
                                  }`}
                                >
                                  <div className="flex-1">
                                    <Label 
                                      htmlFor={perm.key}
                                      className="text-sm cursor-pointer"
                                    >
                                      {perm.description}
                                    </Label>
                                    <p className="text-xs text-muted-foreground">
                                      {perm.help_text || perm.key}
                                      {roleDefault && (
                                        <span className="ml-2 text-green-600">(role default)</span>
                                      )}
                                    </p>
                                  </div>
                                  <Switch
                                    id={perm.key}
                                    checked={isChecked}
                                    onCheckedChange={(checked) => handlePermissionToggle(perm.key, checked)}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Action Buttons */}
                  {hasChanges && (
                    <div className="flex justify-end gap-2 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-1" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-1" />
                            Save Changes
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Save Template Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="save-template-modal">
          <div className="bg-background rounded-lg border shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <BookTemplate className="w-5 h-5 text-primary" />
              Save as Template
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Save the current permission set as a reusable template.
            </p>
            <Input
              data-testid="template-name-input"
              placeholder="Template name (e.g. Office Managers)"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTemplate(); }}
              autoFocus
            />
            <p className="text-xs text-muted-foreground mt-2">
              {Object.values(editedPermissions).filter(Boolean).length} permissions will be saved
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowSaveModal(false)} disabled={savingTemplate}>
                Cancel
              </Button>
              <Button data-testid="confirm-save-template" onClick={handleSaveTemplate} disabled={savingTemplate || !templateName.trim()}>
                {savingTemplate ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Save Template
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Template Confirmation Modal */}
      {showApplyConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="apply-template-modal">
          <div className="bg-background rounded-lg border shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Apply Template
            </h3>
            <p className="text-sm text-muted-foreground mb-1">
              Apply <strong>"{showApplyConfirm.name}"</strong> to{' '}
              <strong>{selectedUserData?.name || 'this user'}</strong>?
            </p>
            <p className="text-sm text-amber-600 font-medium mb-4">
              This will replace their current permissions.
            </p>
            <div className="bg-muted/50 rounded p-3 mb-4">
              <p className="text-xs font-medium text-muted-foreground mb-1">Permissions in this template:</p>
              <p className="text-sm">{showApplyConfirm.permission_count} permissions</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowApplyConfirm(null)} disabled={applyingTemplate}>
                Cancel
              </Button>
              <Button
                data-testid="confirm-apply-template"
                onClick={() => handleApplyTemplate(showApplyConfirm)}
                disabled={applyingTemplate}
              >
                {applyingTemplate ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                Apply Template
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
